const STORAGE_KEY = 'prairi.investorPricingReturn';

let restoredThisVisit = false;

export type InvestorPricingReturn = {
  y: number;
  label: string;
};

function pathOnly(path: string): string {
  return path.split('?')[0].split('#')[0];
}

export function shouldRememberInvestorPricingReturn(fromPath: string, toPath: string): boolean {
  return pathOnly(fromPath) === '/investors' && pathOnly(toPath) === '/investors/pricing';
}

export function rememberInvestorPricingReturn(anchor: HTMLElement): void {
  const label = anchor.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  const y = Math.round(anchor.getBoundingClientRect().top + window.scrollY);
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ y, label } satisfies InvestorPricingReturn)
    );
  } catch {
    // Storage can be blocked.
  }
}

export function peekInvestorPricingReturn(): InvestorPricingReturn | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InvestorPricingReturn>;
    if (typeof parsed.y !== 'number') return null;
    return { y: parsed.y, label: typeof parsed.label === 'string' ? parsed.label : '' };
  } catch {
    return null;
  }
}

export function hasInvestorPricingReturn(): boolean {
  return peekInvestorPricingReturn() !== null;
}

export function clearInvestorPricingReturn(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

/** Scroll to the CTA they used. Returns false if the page is not ready yet. */
export function restoreInvestorPricingReturn(): boolean {
  const saved = peekInvestorPricingReturn();
  if (!saved) return false;

  // Builder paints a short page first; wait until the remembered offset exists
  // or we would scroll the same button while it is still near the top.
  if (document.documentElement.scrollHeight <= saved.y + 80) {
    return false;
  }

  if (saved.label) {
    const matches = Array.from(document.querySelectorAll('a')).filter(anchor => {
      const text = anchor.textContent?.replace(/\s+/g, ' ').trim() ?? '';
      if (text !== saved.label) return false;
      return anchor.getBoundingClientRect().height > 8;
    });
    if (matches.length === 0) return false;

    const closest = matches.reduce(
      (best, el) => {
        const y = el.getBoundingClientRect().top + window.scrollY;
        return Math.abs(y - saved.y) < Math.abs(best.y - saved.y) ? { el, y } : best;
      },
      { el: matches[0], y: matches[0].getBoundingClientRect().top + window.scrollY }
    );
    closest.el.scrollIntoView({ block: 'center', behavior: 'auto' });
    restoredThisVisit = true;
    clearInvestorPricingReturn();
    return true;
  }

  document.documentElement.scrollTop = Math.max(0, saved.y);
  window.scrollTo(0, Math.max(0, saved.y));
  restoredThisVisit = true;
  clearInvestorPricingReturn();
  return true;
}

/** After a successful restore, later ScrollToTop runs must not jump back to 0. */
export function shouldSkipInvestorScrollToTop(pathname: string): boolean {
  if (pathOnly(pathname) !== '/investors') {
    restoredThisVisit = false;
    return false;
  }
  return restoredThisVisit || hasInvestorPricingReturn();
}

/** Keep trying until Builder finishes laying out the remembered CTA. */
export function watchInvestorPricingReturn(timeoutMs = 20000): () => void {
  if (!hasInvestorPricingReturn()) return () => undefined;

  let cancelled = false;
  let timeoutId = 0;
  let intervalId = 0;
  let observer: MutationObserver | null = null;

  const stop = () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
    window.clearInterval(intervalId);
    observer?.disconnect();
  };

  const tick = () => {
    if (cancelled) return;
    if (restoreInvestorPricingReturn() || !hasInvestorPricingReturn()) {
      stop();
    }
  };

  observer = new MutationObserver(tick);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  intervalId = window.setInterval(tick, 100);
  timeoutId = window.setTimeout(stop, timeoutMs);
  tick();
  return stop;
}
